import classNames from "classnames";
import {format, parse} from "date-fns";
import {useState} from "react";
import {pharmacy as strings} from "strings";

import Divider from "components/Divider";
import LoadingIndicator from "components/LoadingIndicator";
import Map from "components/Map";
import PageTitle from "components/PageTitle";
import StyledModal from "components/StyledModal";

import * as models from "liberty-core/api/models";
import {
  formatPhoneNumber,
  getPharmOpenHours,
} from "liberty-core/sharedFunctions";

import {useCurrentPharmacy} from "lib/useCurrentPharmacy";

import styles from "./PharmacyPage.module.css";

type CardProps = {
  className?: string;
  children?: JSX.Element;
};

function Card({className, children}: CardProps) {
  const cardStyle = classNames(styles.card, className);

  return <div className={cardStyle}>{children}</div>;
}

const WEEKDAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
] as const;

function getFormattedTime(time: string) {
  const parsedDate = parse(time, "HH:mm:ss", new Date());

  return format(parsedDate, "p").toLowerCase().replace(" ", "");
}

function indexOfDay(hours: models.BusinessDay[], weekDay: string) {
  return hours.findIndex((h) => h.day === weekDay);
}

function getDayStatus(hours: models.BusinessDay[], weekDay: string) {
  const dayIndex = indexOfDay(hours, weekDay);
  if (dayIndex > -1) {
    return `${getFormattedTime(hours[dayIndex].open)}-${getFormattedTime(
      hours[dayIndex].close,
    )}`;
  }

  return strings.closed;
}

type HoursProps = {
  className?: string;
  hours: models.BusinessDay[];
  timeZone: string;
};

function Hours({className, hours, timeZone}: HoursProps) {
  const hoursContainerStyle = classNames(styles.hoursContainer, className);
  const hoursStyle = classNames(styles.hoursContent, styles.hours);

  const {isOpen} = getPharmOpenHours(new Date(), hours, timeZone);

  return (
    <div className={hoursContainerStyle}>
      <h3 className={styles.hoursTitle}>
        {hours.length === 0
          ? strings.contact
          : isOpen
          ? strings.openNow
          : strings.closedNow}
      </h3>

      {hours.length ? (
        <div className={styles.hoursList}>
          {hours.map((h) => {
            return (
              <div key={`${h.day}-${h.open}`} className={styles.hoursRow}>
                <div className={styles.dayLabel}>{h.day}</div>
                <div className={hoursStyle}>{`${getFormattedTime(
                  h.open,
                )}-${getFormattedTime(h.close)}`}</div>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

type HoursSummaryProps = {
  onSeeHoursClick: () => void;
} & HoursProps;

function HoursSummary({
  className,
  hours,
  timeZone,
  onSeeHoursClick,
}: HoursSummaryProps) {
  const hoursSummaryStyle = classNames(styles.hoursSummary, className);
  const seeHoursStyle = classNames(styles.hoursSummaryContent, styles.seeHours);

  const {isOpen} = getPharmOpenHours(new Date(), hours, timeZone);

  return (
    <div className={hoursSummaryStyle}>
      <p className={styles.hoursSummaryContent}>
        {hours.length === 0
          ? strings.contact
          : isOpen
          ? strings.open
          : strings.closed}
      </p>
      {isOpen ? (
        <p className={styles.hoursSummaryContent}>
          {getDayStatus(hours, WEEKDAYS[new Date().getDay()])}
        </p>
      ) : null}
      {hours.length ? (
        <button className={seeHoursStyle} onClick={onSeeHoursClick}>
          {strings.seeFullHours}
        </button>
      ) : null}
    </div>
  );
}

type ContentSectionProps = {
  className?: string;
  children: JSX.Element;
};

function ContentSection({className, children}: ContentSectionProps) {
  const sectionStyle = classNames(styles.section, className);

  return (
    <section className={sectionStyle}>
      <div className={styles.contentSection}>{children}</div>
      <Divider />
    </section>
  );
}

type InfoProps = {
  className?: string;
  leftImage?: string;
  title: string;
  children: string;
};

function Info({className, leftImage, title, children}: InfoProps) {
  const infoStyle = classNames(styles.info, className);
  return (
    <div className={infoStyle}>
      {leftImage ? (
        <img className={styles.infoImg} alt="" src={leftImage} />
      ) : null}
      <div className={styles.infoContent}>
        <h2 className={styles.h2}>{title}</h2>
        <p>{children}</p>
      </div>
    </div>
  );
}

export default function PharmacyPage() {
  const [pharmacy, loadingPharmacy] = useCurrentPharmacy();
  const [showHoursModal, setShowHoursModal] = useState(false);

  if (pharmacy && !pharmacy.name) {
    return (
      <div className={styles.centeredContainer}>{strings.doesNotExist}</div>
    );
  }

  if (!pharmacy && !loadingPharmacy) {
    return (
      <div className={styles.centeredContainer}>{strings.loadingError}</div>
    );
  }

  if (!pharmacy) {
    return (
      <div className={styles.centeredContainer}>
        <LoadingIndicator />
      </div>
    );
  }

  const locQueryStr = `${pharmacy.name},${pharmacy.address},${pharmacy.address.city},${pharmacy.address.zip},${pharmacy.latitude},${pharmacy.longitude}`;
  const addressLink = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    locQueryStr,
  )}`;

  const mainImgMobileStyle = classNames(styles.mainImg, styles.mobileOnly);
  const mainImgDesktopSectionStyle = classNames(
    styles.section,
    styles.desktopOnly,
  );

  return (
    <div className={styles.pageContainer}>
      {pharmacy.storeImageUrl ? (
        <img
          className={mainImgMobileStyle}
          alt={pharmacy.name}
          src={pharmacy.storeImageUrl}
        />
      ) : null}
      <PageTitle className={styles.title}>{pharmacy.name}</PageTitle>
      <div className={styles.contentColumn}>
        {pharmacy.storeImageUrl ? (
          <section className={mainImgDesktopSectionStyle}>
            <img
              className={styles.mainImg}
              alt={pharmacy.name}
              src={pharmacy.storeImageUrl}
            />
          </section>
        ) : null}

        <section className={styles.section}>
          <div className={styles.cardRow}>
            <Card className={styles.imgCard}>
              <div className={styles.logoImgContainer}>
                <img
                  className={styles.logoImg}
                  alt={pharmacy.name}
                  src={pharmacy.storeLogoUrl}
                />
              </div>
            </Card>
            <Card className={classNames(styles.hoursCard, styles.desktopOnly)}>
              <Hours
                hours={pharmacy.businessHours}
                timeZone={pharmacy.timeZone.baseUtcOffset}
              />
            </Card>
            <Card className={styles.mobileOnly}>
              <HoursSummary
                hours={pharmacy.businessHours}
                timeZone={pharmacy.timeZone.baseUtcOffset}
                onSeeHoursClick={() => setShowHoursModal(true)}
              />
            </Card>
            <StyledModal
              className={styles.seeHoursModal}
              visible={showHoursModal}
              onRequestClose={() => setShowHoursModal(false)}
            >
              <Hours
                className={styles.hoursContainerModal}
                hours={pharmacy.businessHours}
                timeZone={pharmacy.timeZone.baseUtcOffset}
              />
            </StyledModal>
          </div>
        </section>

        {pharmacy.welcomeBody ? (
          <section className={styles.section}>
            <div className={styles.welcomeBody}>{pharmacy.welcomeBody}</div>
          </section>
        ) : null}

        <section className={styles.section}>
          <Map
            className={styles.map}
            center={{
              lat: +pharmacy.latitude,
              lng: +pharmacy.longitude,
            }}
          />
        </section>

        <ContentSection>
          <div className={styles.contactInfo}>
            <a
              className={styles.addressLink}
              href={addressLink}
              target="_blank"
              rel="noreferrer"
            >
              {`${pharmacy.address.street1} ${pharmacy.address.street2 ?? ""}`}
              <br className={styles.addressLineBreak} />
              {`${pharmacy.address.city}, ${pharmacy.address.state} ${pharmacy.address.zip}`}
            </a>
            <a className={styles.phoneLink} href={`tel:${pharmacy.phone}`}>
              {formatPhoneNumber(pharmacy.phone)}
            </a>
          </div>
        </ContentSection>

        {pharmacy.extraInfoList
          ? pharmacy.extraInfoList.map((extraInfo, i) => {
              return (
                <ContentSection key={i}>
                  <Info
                    title={extraInfo.title}
                    leftImage={extraInfo.infoImageUrl ?? undefined}
                  >
                    {extraInfo.description}
                  </Info>
                </ContentSection>
              );
            })
          : null}
      </div>
    </div>
  );
}
